import { useEffect, useRef } from 'react'

/**
 * Watchdog de inactividad del MODO TABLET. Un jugador que se va a la mitad
 * del cuestionario deja la tablet abierta con SU sesión: el siguiente que la
 * agarra escribiría respuestas en el trial de otro. Después de `timeoutMs`
 * sin interacción cerramos la sesión y volvemos a la pantalla de PIN.
 *
 * Deliberadamente local al layout `/tablet` y no global a la web-app: en el
 * celular personal de un jugador (o en el panel de un manager) desloguear
 * sola a los 4 minutos sería un bug, no una protección.
 *
 * `onIdle` se lee por ref para que el efecto se monte una sola vez: si
 * dependiera de la identidad del callback, cada render del layout
 * reinstalaría los listeners y reiniciaría el timer.
 */
export function useIdleLogout(timeoutMs: number, enabled: boolean, onIdle: () => void): void {
  const onIdleRef = useRef(onIdle)
  onIdleRef.current = onIdle

  useEffect(() => {
    if (!enabled) return

    let timer: ReturnType<typeof setTimeout> | undefined
    const arm = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => onIdleRef.current(), timeoutMs)
    }

    // pointerdown/pointermove cubren mouse, touch y el pointer del casco;
    // keydown cubre el teclado en pantalla de la tablet (el teclado virtual
    // no siempre emite pointer events sobre el documento).
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'pointermove', 'keydown', 'wheel']
    for (const ev of events) window.addEventListener(ev, arm, { passive: true })
    arm()

    return () => {
      if (timer) clearTimeout(timer)
      for (const ev of events) window.removeEventListener(ev, arm)
    }
  }, [timeoutMs, enabled])
}
